import { useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/Base/Dialog";
import { Employee } from "@/types/v2";
import { Button } from "@/components/Base/Button";
import { employee as employeeActions } from "@/repositories/employee";
import { captureException } from "@/services/sentry";
import { toast } from "@/hooks/useToast";

type AuthStatus = "active" | "inactive" | "doesNotExist";

interface RestoreEmployeeDialogProps {
  shopId: string;
  employee: Employee;
  open: boolean;
  setOpen: (arg: boolean) => void;
  /** The employee's login status, so the copy matches what they will see. */
  authStatus?: AuthStatus;
  /**
   * A live employee who already has this employee's email. Restoring would
   * leave two employees sharing one login, so the restore is blocked.
   */
  conflictingEmployee?: Employee;
  onRestore?: () => void;
  onComplete?: () => void;
}

const afterRestoreCopy = (authStatus?: AuthStatus) => {
  switch (authStatus) {
    case "inactive":
      return "Their account is disabled and they have no role. Use Activate to re-enable it afterwards.";
    case "doesNotExist":
      return "They have no Shop Hero account. Use Create & Activate afterwards to give them access.";
    case "active":
      return "Their account is still active (it may be shared with another shop), so check their role after restoring.";
    default:
      return "Check their account status after restoring, and activate it if needed.";
  }
};

export const RestoreEmployeeDialog = ({
  employee,
  shopId,
  open,
  setOpen,
  authStatus,
  conflictingEmployee,
  onRestore,
  onComplete,
}: RestoreEmployeeDialogProps) => {
  // The ref blocks a second click before React re-renders; the state is what
  // puts the button into its loading/disabled look.
  const inFlight = useRef(false);
  const [submitting, setSubmitting] = useState(false);

  const handleRestore = async () => {
    if (inFlight.current || conflictingEmployee) return;
    inFlight.current = true;
    setSubmitting(true);
    onRestore?.();
    try {
      await employeeActions.restore(shopId, employee.id);
      toast({ title: "Team member restored", variant: "success" });
    } catch (error) {
      captureException(error);
      toast({
        title: "Unable to restore this team member. Please try again.",
        variant: "destructive",
      });
    }
    inFlight.current = false;
    setSubmitting(false);
    setOpen(false);
    onComplete?.();
  };

  return (
    // Dismissing mid-write would look like a cancel while the restore still
    // goes through, so ignore every close request until it finishes.
    <Dialog onOpenChange={(next) => !submitting && setOpen(next)} open={open}>
      <DialogContent className="max-w-screen-md">
        <DialogHeader>
          <DialogTitle>Restore Team Member</DialogTitle>
        </DialogHeader>
        <DialogDescription className={"text-base"}>
          {`You are about to restore ${employee.firstName} ${employee.lastName}.`}
        </DialogDescription>
        {conflictingEmployee ? (
          <DialogDescription className="text-destructive">
            {`${conflictingEmployee.firstName} ${conflictingEmployee.lastName} is already a team member with the same email (${employee.email}). Restoring this record would leave two team members sharing one login. Delete or change the email of the other team member first.`}
          </DialogDescription>
        ) : (
          <DialogDescription>{afterRestoreCopy(authStatus)}</DialogDescription>
        )}
        <DialogFooter>
          <div className="mt-5">
            <Button
              className="mr-5 mt-5"
              variant={"secondary"}
              onClick={() => setOpen(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              className="mt-5"
              onClick={handleRestore}
              loading={submitting}
              disabled={!!conflictingEmployee}
            >
              Restore
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
